<template>
	<div>
		<device-finder />
		<device-state id="deviceState" v-if="selectedColor" />
		<template
			v-if="selectedQuality || (appraisal && defaultQualityConditions.length)"
		>
			<div id="deviceForms" class="row deviceForms">
				<div class="col">
					<appraisal-form />
				</div>
				<div class="col">
					<contract-form />
				</div>
				<div
					id="canNotBeBuyedOut"
					class="row buyout__error"
					v-if="canNotBeBuyout"
				>
					<div class="col text-center">
						<div class="buyout__errorText">
							{{ $t('buyout.bad_quality') }}
						</div>
						<div class="buyout__errorTitle">
							{{ $t('buyout.can_not_be_buyout') }}
						</div>
					</div>
				</div>
			</div>
			<bonus
				v-if="
					campaign &&
					bonusType === 'bonus_select_list' &&
					(campaign.buyout_bonuses.length > 0 ||
						(!isRegistration && selectedBonus))
				"
			>
				<template #bonusPhoneTitle>
					<slot name="bonusPhoneTitle">
						{{
							$t('buyout.bonus.select_your_new_device', [campaign.new_devices])
						}}
					</slot>
				</template>
			</bonus>
			<device-summary id="device-sum" v-if="!displayThankYou && !canNotBeBuyout && selectedQuality" />
			<additional-info-form v-if="userBuyoutCode" />
			<template v-if="!userBuyoutCode && !canNotBeBuyout">
				<v-checkbox
					v-if="isRegistration"
					v-model="privacyPolicyCheck"
					class="confirm_agreement"
				>
					<template slot="label">
						<slot name="privacyPolicyText">
							<p v-html="privacyPolicyText"></p>
						</slot>
					</template>
				</v-checkbox>
				<slot name="consentToMarketing">
					<v-checkbox
						v-if="isRegistration"
						v-model="isConsentToMarketing"
						class="confirm_agreement"
					>
						<template slot="label">
							<slot name="consentToMarketingText">
								<p v-html="consentToMarketingText"></p>
							</slot>
						</template>
					</v-checkbox>
				</slot>
			</template>
			<button
				v-if="(!canNotBeBuyout && !displayThankYou)"
				id="send"
				class="buyout__btn"
				:disabled="
					(bonusType === 'bonus_select_list' &&
						(!selectedBonus || !selectedPartner) &&
						campaign.buyout_bonuses.length > 0) ||
					disableBuyoutSendBtn ||
					(isRegistration && !privacyPolicyCheck) ||
					!getIsBuyoutEditable
				"
				@click="appraisalSend"
			>
				{{ $t(`buyout.${isRegistration ? 'send' : 'save'}`) }}
			</button>
		</template>
		<slot name="thankYouDialog">
			<div id="thankYouDialog" class="row buyout__order" v-if="displayThankYou">
				<div class="col text-center">
					<div class="green__wrapper info__wrapper">
						<div class="buyout__orderImg">
							<img :src="buildImageUrl(selectedColor.image)" alt="..." />
						</div>
						<div
							class="buyout__orderTitle"
							v-html="
								$sanitize(
									$t(`buyout.thankYou.onlineBuyout.title`, [userBuyoutCode])
								)
							"
						></div>
						<div class="buyout__orderText">
							{{ $t(`buyout.thankYou.onlineBuyout.text`) }}
						</div>
					</div>
				</div>
			</div>
		</slot>
	</div>
</template>

<script>
import DeviceFinder from '../DeviceFinder/App';
import ContractForm from '../ContractForm/App';
import AppraisalForm from '../AppraisalForm/App';
import DeviceState from '../DeviceState/App';
import DeviceSummary from '../DeviceSummary/App';
import Bonus from '../Bonus/App';
import AdditionalInfoForm from '../AdditionalInfoForm/App';
import { buildImageUrl } from '../../../../js/buyout';

import { GETTER_TYPES } from '../../../store/buyout/_getter-types';
import { MUTATION_TYPES } from '../../../store/buyout/_mutation-types';
import { ACTION_TYPES } from '../../../store/buyout/_action-types';
import { VUEX_BUYOUT } from '../../../store/buyout';
import { mapGetters, mapState, mapActions, mapMutations } from 'vuex';
import { config } from '../../../config/buyout';
import { sleep } from '../../../../js/buyout';
import SanitizeMixin from '../../../mixins/buyout/sanitize';

export default {
	name: 'Buyout',
	mixins: [SanitizeMixin],
	components: {
	DeviceFinder,
	ContractForm,
	AppraisalForm,
	DeviceState,
	DeviceSummary,
	Bonus,
	AdditionalInfoForm
	},
	data() {
		return {
			privacyPolicyCheck: false
		}
	},
	async created() {
		if (!this[GETTER_TYPES.QUALITY_TYPES].length) await this[ACTION_TYPES.LOAD_QUALITY_TYPES]();
		if (!this[GETTER_TYPES.STATE_TYPES].length) await this[ACTION_TYPES.LOAD_QUALITY_TYPES]();
	},
	computed: {
		...mapState(VUEX_BUYOUT.NAMESPACE, [
			'appraisal',
			'defaultQualityConditions',
			'selectedColor',
			'userBuyoutCode',
			'selectedQuality',
			'displayThankYou',
			'canNotBeBuyout',
      'isRegistration',
			'campaign',
			'bonusType',
			'selectedBonus',
			'selectedPartner',
			'disableBuyoutSendBtn'
		]),
		...mapGetters(VUEX_BUYOUT.NAMESPACE, [
			GETTER_TYPES.CONSENT_TO_MARKETING,
			GETTER_TYPES.QUALITY_TYPES,
			GETTER_TYPES.STATE_TYPES,
			GETTER_TYPES.IS_BUYOUT_EDITABLE,
			GETTER_TYPES.GET_BUYOUT_PAYLOAD
		]),
		getIsBuyoutEditable: {
			get() {
				return this[GETTER_TYPES.IS_BUYOUT_EDITABLE];
			},
		},
		isConsentToMarketing: {
			get() {
				return this[GETTER_TYPES.CONSENT_TO_MARKETING];
			},
			set(v) {
				this[MUTATION_TYPES.SET_CONSENT_TO_MARKETING](v);
			}
		},
		privacyPolicyText() {
			const link = `<a href="${config.privacy_policy.link}">${config.privacy_policy.link_title}</a>`;
			return (
				config.privacy_policy &&
				this.$sanitize(config.privacy_policy.text.replace('{X}', link))
			);
		},
		consentToMarketingText() {
			return this.$t('buyout.consent_to_marketing');
		}
	},
	methods: {
		...mapMutations(VUEX_BUYOUT.NAMESPACE, [
			MUTATION_TYPES.SET_CONSENT_TO_MARKETING
		]),
		...mapActions(VUEX_BUYOUT.NAMESPACE, [
			ACTION_TYPES.SEND_APPRAISAL,
			ACTION_TYPES.LOAD_QUALITY_TYPES,
			ACTION_TYPES.LOAD_STATE_TYPES,
		]),
		async appraisalSend() {
			return await this[ACTION_TYPES.SEND_APPRAISAL]({payload: this[GETTER_TYPES.GET_BUYOUT_PAYLOAD]});
		},
		buildImageUrl(url) {
			return buildImageUrl(url);
		}
	},
	watch: {
		async canNotBeBuyout(newValue) {
			if (newValue && config.scrollToElements) {
				await sleep(100);
				this.$scrollTo('#canNotBeBuyedOut', { offset: -400, duration: 500 });
			}
		}
	}
}
</script>
